import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getSystemLogs, getOrderModerationLogs, ALLOWED_ROLES } from '../api';
import AdminTransactions from './AdminTransactions';
import SearchPanel from '../components/SearchPanel';
import Icon from '../components/Icon';
import AnimatedEmptyState from '../components/AnimatedEmptyState';
import LogsSortFiltersMenu from '../components/LogsSortFiltersMenu';
import OrderLogsSortFiltersMenu from '../components/OrderLogsSortFiltersMenu';
import PageSizeDropdown from '../components/PageSizeDropdown';
import CopyButton from '../components/CopyButton';
import '../css/home.css';
import { pushDetailNavState } from '../utils/detailNavState';
import '../css/pagination.css';
import '../css/list-summary.css';

const escapeCsvCell = (val) => {
  const s = val == null ? '' : String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const PayloadViewer = ({ payload, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="log-item-payload">
        <button
          type="button"
          className="payload-header"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsOpen(!isOpen); }}
        >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon name="database" size={14} />
          <span>{t('home.logs.payload')}</span>
        </div>
        <Icon name="chevron-down" size={16} className={`payload-chevron ${isOpen ? 'open' : ''}`} />
      </button>
      <div className={`payload-body ${isOpen ? 'open' : ''}`}>
        <div className="payload-content">
          <pre>
            {(() => {
              try {
                const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
                return JSON.stringify(parsed, null, 2);
              } catch (e) {
                return payload;
              }
            })()}
          </pre>
        </div>
      </div>
    </div>
  );
};

const EMPTY_PAGINATION = {
  totalPages: 0,
  totalElements: 0,
  size: 10,
  number: 0,
  first: true,
  last: true
};

const getStoredUserRole = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw)?.role?.toString().trim().toUpperCase() : null;
  } catch {
    return null;
  }
};

const Home = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Вкладка "Транзакции" — журнал операций по балансам, видна только ADMIN и DEV.
  const isAdminOrAbove = useMemo(() => {
    const role = getStoredUserRole();
    return role === ALLOWED_ROLES.ADMIN || role === ALLOWED_ROLES.DEV;
  }, []);

  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('homeActiveTab') || 'system';
    return saved === 'transactions' && !isAdminOrAbove ? 'system' : saved;
  });

  /* ----------------------------- System logs ----------------------------- */
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  const [paginationInfo, setPaginationInfo] = useState({ ...EMPTY_PAGINATION });

  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem('logsFilters');
    if (savedFilters) {
      try {
        return JSON.parse(savedFilters);
      } catch (e) {
        console.error('Ошибка восстановления фильтров:', e);
      }
    }
    return {
      sort: 'createdAt',
      direction: 'DESC',
      targetId: '',
      actorId: '',
      actionContains: '',
      from: '',
      to: ''
    };
  });

  /* ------------------------- Order moderation logs ------------------------ */
  const [orderLogs, setOrderLogs] = useState([]);
  const [orderLogsLoading, setOrderLogsLoading] = useState(false);
  const [orderCurrentPage, setOrderCurrentPage] = useState(0);
  const [orderPageSize, setOrderPageSize] = useState(10);
  const [expandedOrderLogs, setExpandedOrderLogs] = useState(new Set());
  const [orderPaginationInfo, setOrderPaginationInfo] = useState({ ...EMPTY_PAGINATION });
  const orderLoadedRef = useRef(false);

  const [orderFilters, setOrderFilters] = useState(() => {
    const saved = localStorage.getItem('orderLogsFilters');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Ошибка восстановления фильтров ордеров:', e);
      }
    }
    return { sort: 'createdAt', direction: 'DESC', userId: '' };
  });
  const [orderSearchValue, setOrderSearchValue] = useState(() => {
    const saved = localStorage.getItem('orderLogsFilters');
    if (saved) {
      try {
        return JSON.parse(saved).userId || '';
      } catch (e) {
        return '';
      }
    }
    return '';
  });

  const locale = useMemo(() => {
    if (i18n.language === 'ro') return 'ro-RO';
    if (i18n.language === 'ru') return 'ru-RU';
    return 'en-US';
  }, [i18n.language]);

  const roleLabels = useMemo(() => t('users.roles', { returnObjects: true }), [t]);
  const isFirstLoad = useRef(true);

  const topPagerRef = useRef(null);
  const orderTopPagerRef = useRef(null);
  const shouldScrollToPagerRef = useRef(false);
  const orderShouldScrollRef = useRef(false);

  const loadLogs = async (page = 0, size = pageSize) => {
    setLogsLoading(true);
    try {
      const response = await getSystemLogs({
        page,
        size,
        targetId: filters.targetId || undefined,
        actorId: filters.actorId || undefined,
        actionContains: filters.actionContains || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
        sort: filters.sort || 'createdAt',
        direction: filters.direction || 'DESC'
      });
      setLogs(response.content || []);
      setPaginationInfo({
        totalPages: response.totalPages || 0,
        totalElements: response.totalElements || 0,
        size: response.size || size,
        number: response.number || page,
        first: response.first || false,
        last: response.last || false
      });
    } catch (err) {
      console.error('Ошибка загрузки логов:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  const loadOrderLogs = async (page = 0, size = orderPageSize, activeFilters = orderFilters) => {
    setOrderLogsLoading(true);
    try {
      const response = await getOrderModerationLogs({
        page,
        size,
        sort: activeFilters.sort || 'createdAt',
        direction: activeFilters.direction || 'DESC',
        userId: activeFilters.userId || undefined
      });
      setOrderLogs(response.content || []);
      setOrderPaginationInfo({
        totalPages: response.totalPages || 0,
        totalElements: response.totalElements || 0,
        size: response.size || size,
        number: response.number || page,
        first: response.first || false,
        last: response.last || false
      });
    } catch (err) {
      console.error('Ошибка загрузки логов ордеров:', err);
    } finally {
      setOrderLogsLoading(false);
    }
  };

  const [exportingAllOrderLogs, setExportingAllOrderLogs] = useState(false);

  const downloadOrderLogsCsv = (list, filenamePrefix) => {
    const headers = ['title', 'description', 'aiError', 'userId'];
    const rows = list.map((log) =>
      [log.title ?? '', log.description ?? '', log.aiError ?? '', log.userId ?? ''].map(escapeCsvCell)
    );
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\r\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportOrderLogsCsv = () => {
    if (!orderLogs.length) {
      window.alert(t('home.orderLogs.csvExportEmpty'));
      return;
    }
    downloadOrderLogsCsv(orderLogs, 'order-logs-page');
  };

  const handleExportAllOrderLogsCsv = async () => {
    if (exportingAllOrderLogs) return;
    setExportingAllOrderLogs(true);
    try {
      const response = await getOrderModerationLogs({
        page: 0,
        size: 100000,
        sort: orderFilters.sort || 'createdAt',
        direction: orderFilters.direction || 'DESC',
        userId: orderFilters.userId || undefined
      });
      const all = response.content || [];
      if (!all.length) {
        window.alert(t('home.orderLogs.csvExportEmpty'));
        return;
      }
      downloadOrderLogsCsv(all, 'order-logs-all');
    } catch (err) {
      window.alert(err.message || t('home.orderLogs.csvExportError'));
    } finally {
      setExportingAllOrderLogs(false);
    }
  };

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;

      // Восстанавливаем страницу и размер страницы при возврате с юзер-деталс (см. fromLogs)
      const restore = location.state?.fromLogs;
      const restoredTab = restore?.tab;
      if (restoredTab && restoredTab !== activeTab) {
        setActiveTab(restoredTab);
      }

      const systemPage = restoredTab === 'system' ? (restore.page || 0) : 0;
      const systemSize = restoredTab === 'system' ? (restore.size || pageSize) : pageSize;
      if (restoredTab === 'system') {
        setCurrentPage(systemPage);
        setPageSize(systemSize);
      }
      loadLogs(systemPage, systemSize);

      if (restoredTab === 'orders' || activeTab === 'orders') {
        orderLoadedRef.current = true;
        const orderPage = restoredTab === 'orders' ? (restore.page || 0) : 0;
        const orderSize = restoredTab === 'orders' ? (restore.size || orderPageSize) : orderPageSize;
        if (restoredTab === 'orders') {
          setOrderCurrentPage(orderPage);
          setOrderPageSize(orderSize);
        }
        loadOrderLogs(orderPage, orderSize);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem('logsFilters', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    localStorage.setItem('orderLogsFilters', JSON.stringify(orderFilters));
  }, [orderFilters]);

  // Переключение вкладки: лениво подгружаем логи ордеров при первом открытии
  useEffect(() => {
    localStorage.setItem('homeActiveTab', activeTab);
    if (activeTab === 'orders' && !orderLoadedRef.current) {
      orderLoadedRef.current = true;
      loadOrderLogs(0, orderPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleReset = () => {
    const defaultFilters = {
      sort: 'createdAt',
      direction: 'DESC',
      targetId: '',
      actorId: '',
      actionContains: '',
      from: '',
      to: ''
    };
    setFilters(defaultFilters);
    setCurrentPage(0);
    setTimeout(() => {
      loadLogs(0, pageSize);
    }, 0);
  };

  const handleOrderReset = () => {
    const defaults = { sort: 'createdAt', direction: 'DESC', userId: '' };
    setOrderFilters(defaults);
    setOrderSearchValue('');
    setOrderCurrentPage(0);
    setTimeout(() => {
      loadOrderLogs(0, orderPageSize, defaults);
    }, 0);
  };

  const handlePageChange = (page, shouldScroll = false) => {
    if (shouldScroll) {
      shouldScrollToPagerRef.current = true;
    }
    setCurrentPage(page);
    loadLogs(page, pageSize);
  };

  const handlePageSizeChange = (newSize) => {
    shouldScrollToPagerRef.current = true;
    setPageSize(parseInt(newSize, 10));
    setCurrentPage(0);
    loadLogs(0, parseInt(newSize, 10));
  };

  const handleOrderPageChange = (page, shouldScroll = false) => {
    if (shouldScroll) {
      orderShouldScrollRef.current = true;
    }
    setOrderCurrentPage(page);
    loadOrderLogs(page, orderPageSize);
  };

  const handleOrderPageSizeChange = (newSize) => {
    orderShouldScrollRef.current = true;
    setOrderPageSize(parseInt(newSize, 10));
    setOrderCurrentPage(0);
    loadOrderLogs(0, parseInt(newSize, 10));
  };

  const updateOrderFilter = (patch) => {
    const next = { ...orderFilters, ...patch };
    setOrderFilters(next);
    setOrderCurrentPage(0);
    loadOrderLogs(0, orderPageSize, next);
  };

  useEffect(() => {
    if (!shouldScrollToPagerRef.current) return;
    if (logsLoading) return;

    const el = topPagerRef.current;
    if (!el) {
      const tId = setTimeout(() => {
        const elRetry = topPagerRef.current;
        if (!shouldScrollToPagerRef.current || !elRetry) return;
        shouldScrollToPagerRef.current = false;
        elRetry.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
      return () => clearTimeout(tId);
    }

    shouldScrollToPagerRef.current = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [currentPage, pageSize, logsLoading]);

  useEffect(() => {
    if (!orderShouldScrollRef.current) return;
    if (orderLogsLoading) return;

    const el = orderTopPagerRef.current;
    if (!el) {
      const tId = setTimeout(() => {
        const elRetry = orderTopPagerRef.current;
        if (!orderShouldScrollRef.current || !elRetry) return;
        orderShouldScrollRef.current = false;
        elRetry.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
      return () => clearTimeout(tId);
    }

    orderShouldScrollRef.current = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [orderCurrentPage, orderPageSize, orderLogsLoading]);

  const getVisiblePages = (current, totalPages) => {
    if (totalPages <= 1) return [];
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);

    const pages = [0];
    const left = Math.max(1, current - 1);
    const right = Math.min(totalPages - 2, current + 1);
    if (left > 1) pages.push('left-ellipsis');
    for (let p = left; p <= right; p += 1) pages.push(p);
    if (right < totalPages - 2) pages.push('right-ellipsis');
    pages.push(totalPages - 1);
    return pages;
  };

  const renderPager = ({
    position,
    current,
    info,
    size,
    loading,
    itemsLength,
    onPageChange,
    onPageSizeChange,
    pagerRef
  }) => {
    if ((loading && itemsLength === 0) || info.totalPages <= 0) return null;
    const isFirst = current <= 0;
    const isLast = current >= info.totalPages - 1;
    const pages = getVisiblePages(current, info.totalPages || 0);

    return (
      <div
        className={`orders-pager orders-pager-${position}`}
        ref={position === 'top' ? pagerRef : null}
      >
        <div className="orders-pager-info">
          {t('common.pagination.showing', {
            from: info.totalElements > 0 ? current * size + 1 : 0,
            to: Math.min((current + 1) * size, info.totalElements),
            total: info.totalElements,
            defaultValue: `Показано ${
              info.totalElements > 0 ? current * size + 1 : 0
            } - ${Math.min((current + 1) * size, info.totalElements)} из ${info.totalElements}`
          })}
        </div>

        <div className="orders-pager-controls">
          <button
            type="button"
            className="orders-pager-nav"
            onClick={() => onPageChange(current - 1, position === 'bottom')}
            disabled={isFirst}
          >
            {t('common.pagination.previous', { defaultValue: 'Назад' })}
          </button>

          <div className="orders-pager-pages">
            {pages.map((item) => {
              if (typeof item !== 'number') {
                return <span key={item} className="orders-pager-ellipsis">...</span>;
              }
              const active = item === current;
              return (
                <button
                  key={item}
                  type="button"
                  className={`orders-pager-page ${active ? 'active' : ''}`}
                  onClick={() => onPageChange(item, position === 'bottom')}
                  aria-current={active ? 'page' : undefined}
                >
                  {item + 1}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="orders-pager-nav"
            onClick={() => onPageChange(current + 1, position === 'bottom')}
            disabled={isLast}
          >
            {t('common.pagination.next', { defaultValue: 'Вперед' })}
          </button>

          <PageSizeDropdown
            value={size}
            onChange={onPageSizeChange}
            options={[10, 20, 50, 100]}
            position={position}
          />
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString(locale, {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    });
  };

  const getLogTypeInfo = (type, action) => {
    const t = (type || '').toLowerCase();
    const a = (action || '').toLowerCase();

    if (a.includes('login')) return { icon: 'log-in', color: 'var(--accent-primary)', bg: 'rgba(37, 99, 235, 0.1)' };
    if (a.includes('logout')) return { icon: 'log-out', color: 'var(--text-secondary)', bg: 'var(--bg-tertiary)' };
    if (t.includes('balance') || a.includes('balance')) {
      if (a.includes('subtract')) {
        return { icon: 'minus-circle', color: '#64748b', bg: 'rgba(100, 116, 139, 0.12)' };
      }
      if (a.includes('add')) {
        return { icon: 'plus-circle', color: 'var(--accent-success)', bg: 'rgba(16, 185, 129, 0.1)' };
      }
      return { icon: 'credit-card', color: 'var(--accent-primary)', bg: 'rgba(37, 99, 235, 0.1)' };
    }
    if (a.includes('create') || a.includes('add') || a.includes('register')) return { icon: 'user-plus', color: 'var(--accent-success)', bg: 'rgba(16, 185, 129, 0.1)' };
    if (t.includes('security') || t.includes('admin')) return { icon: 'shield', color: 'var(--accent-error)', bg: 'rgba(239, 68, 68, 0.1)' };
    if (t.includes('error') || t.includes('fail') || a.includes('error')) return { icon: 'alert-triangle', color: 'var(--accent-error)', bg: 'rgba(239, 68, 68, 0.1)' };
    if (t.includes('success') || a.includes('success')) return { icon: 'check-circle', color: 'var(--accent-success)', bg: 'rgba(16, 185, 129, 0.1)' };
    if (t.includes('data') || t.includes('system')) return { icon: 'database', color: 'var(--accent-primary)', bg: 'rgba(37, 99, 235, 0.1)' };

    return { icon: 'server', color: 'var(--text-secondary)', bg: 'var(--bg-tertiary)' };
  };

  const getOrderLogStatus = (log) => {
    if (log.aiError) {
      return { icon: 'alert-triangle', color: 'var(--accent-error)', bg: 'rgba(239, 68, 68, 0.1)' };
    }
    return { icon: 'check-circle', color: 'var(--accent-success)', bg: 'rgba(16, 185, 129, 0.1)' };
  };

  const toggleLogExpanded = (logId) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const toggleOrderLogExpanded = (logId) => {
    setExpandedOrderLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const [searchValue, setSearchValue] = useState(filters.actionContains);

  // Sync internal search value with filters (e.g. on reset)
  useEffect(() => {
    setSearchValue(filters.actionContains);
  }, [filters.actionContains]);

  // Debounce search input
  useEffect(() => {
    if (isFirstLoad.current) return;
    const timer = setTimeout(() => {
      if (filters.actionContains !== searchValue) {
        const nextFilters = { ...filters, actionContains: searchValue };
        setFilters(nextFilters);
        setCurrentPage(0);
        // Manual load for debounce since we don't have a single useEffect tracking all filters in Home.jsx
        setLogsLoading(true);
        getSystemLogs({
          page: 0,
          size: pageSize,
          targetId: nextFilters.targetId || undefined,
          actorId: nextFilters.actorId || undefined,
          actionContains: nextFilters.actionContains || undefined,
          from: nextFilters.from || undefined,
          to: nextFilters.to || undefined,
          sort: nextFilters.sort || 'createdAt',
          direction: nextFilters.direction || 'DESC'
        }).then(response => {
          setLogs(response.content || []);
          setPaginationInfo({
            totalPages: response.totalPages || 0,
            totalElements: response.totalElements || 0,
            size: response.size || pageSize,
            number: response.number || 0,
            first: response.first || false,
            last: response.last || false
          });
        }).finally(() => setLogsLoading(false));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Debounce поиска по ID пользователя на вкладке ордеров
  useEffect(() => {
    if (isFirstLoad.current) return;
    const timer = setTimeout(() => {
      const trimmed = (orderSearchValue || '').trim();
      if ((orderFilters.userId || '') !== trimmed) {
        const next = { ...orderFilters, userId: trimmed };
        setOrderFilters(next);
        setOrderCurrentPage(0);
        loadOrderLogs(0, orderPageSize, next);
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSearchValue]);

  const filtersActive = useMemo(() => {
    const f = filters;
    return Boolean(
      (f.actionContains && String(f.actionContains).trim()) ||
        (f.targetId && String(f.targetId).trim()) ||
        (f.actorId && String(f.actorId).trim()) ||
        f.from ||
        f.to ||
        f.sort !== 'createdAt' ||
        f.direction !== 'DESC'
    );
  }, [filters]);

  const orderFiltersActive = useMemo(() => {
    const f = orderFilters;
    return Boolean(
      (f.userId && String(f.userId).trim()) ||
        f.sort !== 'createdAt' ||
        f.direction !== 'DESC'
    );
  }, [orderFilters]);

  const extraActions = (
    <LogsSortFiltersMenu
      sort={filters.sort}
      direction={filters.direction}
      onSortChange={(val) => {
        const nextFilters = { ...filters, sort: val };
        setFilters(nextFilters);
        setCurrentPage(0);
        loadLogs(0, pageSize);
      }}
      onDirectionChange={(val) => {
        const nextFilters = { ...filters, direction: val };
        setFilters(nextFilters);
        setCurrentPage(0);
        loadLogs(0, pageSize);
      }}
      createdFrom={filters.from}
      createdTo={filters.to}
      targetId={filters.targetId}
      actorId={filters.actorId}
      onDateFromChange={(val) => {
        const nextFilters = { ...filters, from: val };
        setFilters(nextFilters);
        setCurrentPage(0);
        loadLogs(0, pageSize);
      }}
      onDateToChange={(val) => {
        const nextFilters = { ...filters, to: val };
        setFilters(nextFilters);
        setCurrentPage(0);
        loadLogs(0, pageSize);
      }}
      onTargetIdChange={(val) => {
        const nextFilters = { ...filters, targetId: val };
        setFilters(nextFilters);
        setCurrentPage(0);
        loadLogs(0, pageSize);
      }}
      onActorIdChange={(val) => {
        const nextFilters = { ...filters, actorId: val };
        setFilters(nextFilters);
        setCurrentPage(0);
        loadLogs(0, pageSize);
      }}
      onReset={handleReset}
    />
  );

  const tabs = [
    { id: 'system', icon: 'database', label: t('home.tabs.system') },
    { id: 'orders', icon: 'package', label: t('home.tabs.orders') }
  ];
  if (isAdminOrAbove) {
    tabs.push({ id: 'transactions', icon: 'credit-card', label: t('home.tabs.transactions') });
  }

  const renderSystemTab = () => (
    <>
      <div className="orders-content-surface" style={{ marginBottom: '24px' }}>
        <div className="orders-controls-surface" style={{ marginBottom: 0 }}>
          <div className="list-summary-row">
            <div className="list-summary-header">
              <div className="list-summary-header-left">
                <div className="list-summary-icon">
                  <Icon name="database" size={24} />
                </div>
                <div>
                  <h3 className="list-summary-title">{t('home.logs.title')}</h3>
                  <p className="list-summary-subtitle">
                    {t('home.logs.subtitle', { count: paginationInfo.totalElements })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="search-panel-wrapper">
            <SearchPanel
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              onReset={handleReset}
              loading={logsLoading}
              placeholder={t('home.logs.filters.actionContainsPlaceholder')}
              extraActions={extraActions}
              filtersActive={filtersActive}
            />
          </div>
        </div>
      </div>

      <div className="orders-content-surface">
        <div className="orders-cards-wrapper">
          {renderPager({
            position: 'top',
            current: currentPage,
            info: paginationInfo,
            size: pageSize,
            loading: logsLoading,
            itemsLength: logs.length,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
            pagerRef: topPagerRef
          })}

          {logsLoading && logs.length === 0 ? (
            <div className="home-logs-loading">
              <div className="strict-loader"></div>
              <span>{t('home.logs.loading')}</span>
            </div>
          ) : logs.length === 0 ? (
            <AnimatedEmptyState
              iconName="complaints"
              title={t('home.logs.emptyTitle')}
              subtitle={t('home.logs.emptyText')}
            />
          ) : (
            <div className="home-logs-list" style={{ opacity: logsLoading ? 0.5 : 1, pointerEvents: logsLoading ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
              {logs.map((log) => {
                const isExpanded = expandedLogs.has(log.id);
                const typeInfo = getLogTypeInfo(log.type, log.action);

                return (
                  <div key={log.id} className={`home-log-item ${isExpanded ? 'expanded' : ''}`}>
                    <div className="log-item-header" onClick={(e) => { e.preventDefault(); toggleLogExpanded(log.id); }}>
                      <div className="log-item-main-info">
                        <div className="log-type-icon-wrapper" style={{ color: typeInfo.color, background: typeInfo.bg }}>
                          <Icon name={typeInfo.icon} size={20} />
                        </div>
                        <div className="log-item-title-group">
                          <div className="log-item-action-row">
                            <div className="log-item-action">{log.action}</div>
                            {log.action && <CopyButton text={log.action} />}
                          </div>
                          <div className="log-type-badge" style={{ color: typeInfo.color, borderColor: typeInfo.bg, background: typeInfo.bg }}>
                            {log.type || 'SYSTEM'}
                          </div>
                        </div>
                      </div>
                      <div className="log-item-header-right">
                        <div className="log-item-date">{formatDate(log.createdAt)}</div>
                        <button
                          type="button"
                          className={`btn-log-expand ${isExpanded ? 'expanded' : ''}`}
                          title={isExpanded ? t('home.logs.collapse') : t('home.logs.expand')}
                        >
                          <Icon name="chevron-down" size={18} />
                        </button>
                      </div>
                    </div>
                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease: 'easeOut' }}
                          className="log-item-expanded-content"
                          style={{ overflow: 'hidden' }}
                        >
                          <div className="log-item-body-content" style={{ paddingBottom: '16px' }}>
                            {log.reason && (
                              <div className="log-item-reason">
                                <Icon name="info" size={14} className="reason-icon" />
                                <span><strong>{t('home.logs.reason')}:</strong> {log.reason}</span>
                              </div>
                            )}

                            <div className="log-item-details">
                              {(log.actor || log.target) && (
                                <div className="log-item-actors-full">
                                  {log.actor && (
                                    <div
                                      className={`log-actor-row ${log.actor.id ? 'clickable' : ''}`}
                                      onClick={(e) => {
                                        if (log.actor.id) {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          navigate(`/users/${log.actor.id}`, {
                                            state: pushDetailNavState(location, {
                                              fromLogs: { path: location.pathname, tab: 'system', page: currentPage, size: pageSize },
                                            }),
                                          });
                                        }
                                      }}
                                    >
                                      <div className="log-actor-row-left">
                                        <div className="log-actor-icon-wrapper">
                                          <Icon name="user-plus" size={18} />
                                        </div>
                                        <div className="log-actor-details">
                                          <div className="log-actor-meta">
                                            <span className="log-actor-label">{t('home.logs.actor')}</span>
                                            {log.actor.role && (
                                              <span className="log-actor-role">{roleLabels?.[log.actor.role] || log.actor.role}</span>
                                            )}
                                          </div>
                                          <div className="log-actor-name">
                                            {log.actor.fullName || log.actor.defaultPhoneNumber || log.actor.id}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {log.target && (
                                    <div
                                      className={`log-actor-row ${log.target.id ? 'clickable' : ''}`}
                                      onClick={(e) => {
                                        if (log.target.id) {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          navigate(`/users/${log.target.id}`, {
                                            state: pushDetailNavState(location, {
                                              fromLogs: { path: location.pathname, tab: 'system', page: currentPage, size: pageSize },
                                            }),
                                          });
                                        }
                                      }}
                                    >
                                      <div className="log-actor-row-left">
                                        <div className="log-actor-icon-wrapper">
                                          <Icon name="shield" size={18} />
                                        </div>
                                        <div className="log-actor-details">
                                          <div className="log-actor-meta">
                                            <span className="log-actor-label">{t('home.logs.target')}</span>
                                            {log.target.role && (
                                              <span className="log-target-role">{roleLabels?.[log.target.role] || log.target.role}</span>
                                            )}
                                          </div>
                                          <div className="log-actor-name">
                                            {log.target.fullName || log.target.defaultPhoneNumber || log.target.id}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {log.payload && (
                                <PayloadViewer payload={log.payload} t={t} />
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}

          {renderPager({
            position: 'bottom',
            current: currentPage,
            info: paginationInfo,
            size: pageSize,
            loading: logsLoading,
            itemsLength: logs.length,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
            pagerRef: topPagerRef
          })}
        </div>
      </div>
    </>
  );

  const renderOrdersTab = () => (
    <>
      <div className="orders-content-surface" style={{ marginBottom: '24px' }}>
        <div className="orders-controls-surface" style={{ marginBottom: 0 }}>
          <div className="list-summary-row">
            <div className="list-summary-header">
              <div className="list-summary-header-left">
                <div className="list-summary-icon">
                  <Icon name="package" size={24} />
                </div>
                <div>
                  <h3 className="list-summary-title">{t('home.orderLogs.title')}</h3>
                  <p className="list-summary-subtitle">
                    {t('home.orderLogs.subtitle', { count: orderPaginationInfo.totalElements })}
                  </p>
                </div>
              </div>
            </div>
            <div className="order-logs-export-wrap">
              <button
                type="button"
                className="btn btn-secondary order-logs-export-button"
                onClick={handleExportOrderLogsCsv}
                disabled={orderLogsLoading || orderLogs.length === 0}
              >
                <Icon name="file-text" size={18} />
                {t('home.orderLogs.exportCsv')}
              </button>
              <button
                type="button"
                className="btn btn-secondary order-logs-export-button"
                onClick={handleExportAllOrderLogsCsv}
                disabled={exportingAllOrderLogs}
              >
                <Icon name="file-text" size={18} />
                {exportingAllOrderLogs ? t('home.orderLogs.exporting') : t('home.orderLogs.exportAll')}
              </button>
            </div>
          </div>

          <div className="search-panel-wrapper">
            <SearchPanel
              searchValue={orderSearchValue}
              onSearchChange={setOrderSearchValue}
              onReset={handleOrderReset}
              loading={orderLogsLoading}
              placeholder={t('home.orderLogs.filters.userIdPlaceholder')}
              extraActions={(
                <OrderLogsSortFiltersMenu
                  sort={orderFilters.sort}
                  direction={orderFilters.direction}
                  onSortChange={(val) => updateOrderFilter({ sort: val })}
                  onDirectionChange={(val) => updateOrderFilter({ direction: val })}
                />
              )}
              filtersActive={orderFiltersActive}
            />
          </div>
        </div>
      </div>

      <div className="orders-content-surface">
        <div className="orders-cards-wrapper">
          {renderPager({
            position: 'top',
            current: orderCurrentPage,
            info: orderPaginationInfo,
            size: orderPageSize,
            loading: orderLogsLoading,
            itemsLength: orderLogs.length,
            onPageChange: handleOrderPageChange,
            onPageSizeChange: handleOrderPageSizeChange,
            pagerRef: orderTopPagerRef
          })}

          {orderLogsLoading && orderLogs.length === 0 ? (
            <div className="home-logs-loading">
              <div className="strict-loader"></div>
              <span>{t('home.orderLogs.loading')}</span>
            </div>
          ) : orderLogs.length === 0 ? (
            <AnimatedEmptyState
              iconName="orders"
              title={t('home.orderLogs.emptyTitle')}
              subtitle={t('home.orderLogs.emptyText')}
            />
          ) : (
            <div className="home-logs-list" style={{ opacity: orderLogsLoading ? 0.5 : 1, pointerEvents: orderLogsLoading ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
              {orderLogs.map((log) => {
                const isExpanded = expandedOrderLogs.has(log.id);
                const status = getOrderLogStatus(log);

                return (
                  <div key={log.id} className={`home-log-item ${isExpanded ? 'expanded' : ''}`}>
                    <div className="log-item-header" onClick={(e) => { e.preventDefault(); toggleOrderLogExpanded(log.id); }}>
                      <div className="log-item-main-info">
                        <div className="log-type-icon-wrapper" style={{ color: status.color, background: status.bg }}>
                          <Icon name={status.icon} size={20} />
                        </div>
                        <div className="log-item-title-group">
                          <div className="log-item-action-row">
                            <div className="log-item-action order-log-title">{log.title || t('home.orderLogs.untitled')}</div>
                            {log.title && <CopyButton text={log.title} />}
                          </div>
                          <div className="order-log-badges">
                            <div className="log-type-badge" style={{ color: status.color, borderColor: status.bg, background: status.bg }}>
                              {log.aiError ? t('home.orderLogs.statusError') : t('home.orderLogs.statusOk')}
                            </div>
                            {log.attemptsCount != null && (
                              <span className="order-log-attempts" title={t('home.orderLogs.attempts', { count: log.attemptsCount })}>
                                <Icon name="refresh" size={12} />
                                {t('home.orderLogs.attempts', { count: log.attemptsCount })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="log-item-header-right">
                        <div className="log-item-date">{formatDate(log.createdAt)}</div>
                        <button
                          type="button"
                          className={`btn-log-expand ${isExpanded ? 'expanded' : ''}`}
                          title={isExpanded ? t('home.logs.collapse') : t('home.logs.expand')}
                        >
                          <Icon name="chevron-down" size={18} />
                        </button>
                      </div>
                    </div>
                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease: 'easeOut' }}
                          className="log-item-expanded-content"
                          style={{ overflow: 'hidden' }}
                        >
                          <div className="log-item-body-content" style={{ paddingBottom: '16px' }}>
                            {log.description && (
                              <div className="log-item-reason">
                                <Icon name="info" size={14} className="reason-icon" />
                                <span><strong>{t('home.orderLogs.description')}:</strong> {log.description}</span>
                              </div>
                            )}

                            {log.aiError && (
                              <div className="order-log-error">
                                <div className="order-log-error-header">
                                  <span className="order-log-error-icon">
                                    <Icon name="alert-triangle" size={14} />
                                  </span>
                                  <span className="order-log-error-label">{t('home.orderLogs.aiError')}</span>
                                </div>
                                <div className="order-log-error-text">{log.aiError}</div>
                              </div>
                            )}

                            <div className="log-item-details">
                              <div className="order-log-meta">
                                {log.userId && (
                                  <div
                                    className="log-actor-row clickable"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      navigate(`/users/${log.userId}`, {
                                        state: pushDetailNavState(location, {
                                          fromLogs: { path: location.pathname, tab: 'orders', page: orderCurrentPage, size: orderPageSize },
                                        }),
                                      });
                                    }}
                                  >
                                    <div className="log-actor-row-left">
                                      <div className="log-actor-icon-wrapper">
                                        <Icon name="user" size={18} />
                                      </div>
                                      <div className="log-actor-details">
                                        <div className="log-actor-meta">
                                          <span className="log-actor-label">{t('home.orderLogs.user')}</span>
                                        </div>
                                        <div className="log-actor-name order-log-id">{log.userId}</div>
                                      </div>
                                    </div>
                                    <span className="order-log-row-chevron">
                                      <Icon name="chevron-right" size={16} />
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}

          {renderPager({
            position: 'bottom',
            current: orderCurrentPage,
            info: orderPaginationInfo,
            size: orderPageSize,
            loading: orderLogsLoading,
            itemsLength: orderLogs.length,
            onPageChange: handleOrderPageChange,
            onPageSizeChange: handleOrderPageSizeChange,
            pagerRef: orderTopPagerRef
          })}
        </div>
      </div>
    </>
  );

  return (
    <div className="orders-page">
      <div className="home-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`home-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {activeTab === tab.id && (
              <motion.div
                layoutId="homeTabActiveBg"
                className="home-tab-active-bg"
                transition={{ type: 'spring', stiffness: 500, damping: 40 }}
              />
            )}
            <span className="home-tab-content">
              <Icon name={tab.icon} size={16} />
              {tab.label}
            </span>
          </button>
        ))}
      </div>

      {activeTab === 'system' && renderSystemTab()}
      {activeTab === 'orders' && renderOrdersTab()}
      {activeTab === 'transactions' && isAdminOrAbove && <AdminTransactions />}
    </div>
  );
};

export default Home;
